/**
 * SessionGeometry adapter — studyMetadata (+ optional Cornerstone IPP) →
 * NavigationController geometry
 *
 * Builds a `SessionGeometry` from the canonical `studyMetadata.series[seriesId]`
 * array, optionally preferring a Cornerstone-backed per-list-index IPP resolver.
 * Encodes the §10 invariants:
 *   - `anatomicSign` derived via `computeListAnatomicSign` (listAnatomicSign authority)
 *   - 0 → -1 mapping: "insufficient data" falls back to PACS default
 *
 * IPP source: the canonical sidecar (`DicomInstanceInfo.image_position_patient`)
 * usually omits IPP on canonical studies (it carries only a scalar, sometimes
 * sign-flipped z_position), so the sidecar-only sign is 0 and the scroll
 * direction falls back to -1 — which is backwards on the plane-reversed
 * coronal/sagittal reformats the canonical pipeline emits (the same failure mode
 * PR #544 fixed for the jump path). Callers that have Cornerstone pass a
 * `resolveIpp(listIndex)` that returns the real per-slice ImagePositionPatient
 * Cornerstone holds (`sliceWorldPositionFromMetadata`), falling back to the
 * sidecar per index when Cornerstone hasn't cached that slice yet. Resolution is
 * lazy (consulted per call), so once the volume has streamed the real IPP
 * supersedes the synthesized eager-cache value and the sign settles correct.
 *
 * The earlier "NOT cornerstone.metaData, it reorders on flipped HTJ2K" concern
 * is the K-step (geometric) reorder; keying the resolver by the stable
 * per-list-index imageId (`imageIds[listIndex]`, the same order as `instances`)
 * sidesteps it — list-index 0 / N-1 map to the right slices regardless.
 *
 * Pure module — no Cornerstone, no React, no atoms (the resolver is injected by
 * the Cornerstone-aware caller). Safe to import in tests.
 */

import type { DicomInstanceInfo } from "@/utils/dicomMetadataLoader";
import { computeListAnatomicSign, type Vec3 } from "@/utils/anatomicScroll";
import type { SessionGeometry } from "./ViewportSession";
import type { PlaneOrientation, WorldPos } from "./types";

/** Resolve the real per-slice IPP for a list index (Cornerstone-backed). */
export type IppResolver = (listIndex: number) => WorldPos | null;

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Create a `SessionGeometry` backed by a canonical-metadata instance array,
 * optionally preferring a Cornerstone per-list-index IPP resolver.
 *
 * @param instances - `studyMetadata.series[seriesId]` — the per-slice DicomInstanceInfo list
 * @param plane - Acquisition plane ("Axial" | "Coronal" | "Sagittal")
 * @param resolveIpp - optional Cornerstone-backed IPP resolver, preferred over the sidecar
 */
export function createStudyMetadataGeometry(
  instances: DicomInstanceInfo[],
  plane: PlaneOrientation,
  resolveIpp?: IppResolver,
): SessionGeometry {
  // Pre-extract sidecar IPPs once so the fallback lookup is O(1) per call.
  const sidecarIpps: (WorldPos | null)[] = instances.map((inst) => {
    const raw = inst.image_position_patient;
    if (!raw || raw.length < 3) return null;
    return [raw[0], raw[1], raw[2]] as WorldPos;
  });

  // Prefer the Cornerstone-backed IPP (real per-slice geometry); fall back to
  // the sidecar per index when Cornerstone hasn't cached that slice yet.
  const ippAt = (listIndex: number): WorldPos | null => {
    if (listIndex < 0 || listIndex >= instances.length) return null;
    return resolveIpp?.(listIndex) ?? sidecarIpps[listIndex] ?? null;
  };

  return {
    totalSlices(): number {
      return instances.length;
    },

    ippFor(listIndex: number): WorldPos | null {
      return ippAt(listIndex);
    },

    // Derived lazily from the first/last IPP each call so a Cornerstone resolver
    // returning the streamed real IPP supersedes the synthesized eager-cache
    // value. computeListAnatomicSign returns -1|0|1; 0 means "insufficient data"
    // → fall back to -1 (PACS default: decreasing list-index = anatomically up).
    anatomicSign(): 1 | -1 {
      const firstIpp = ippAt(0);
      const lastIpp = instances.length > 0 ? ippAt(instances.length - 1) : null;
      const rawSign = computeListAnatomicSign(
        firstIpp as Vec3 | null,
        lastIpp as Vec3 | null,
        plane,
      );
      return rawSign === 1 ? 1 : -1;
    },

    orientation(): PlaneOrientation {
      return plane;
    },
  };
}
